import { addAlertThunk } from 'actions/alerts/AlertsActions';
import { setAuthToken } from 'actions/auth/AuthActions';
import {
    CUSTOMER_AUTHENTICATE_BY_CODE_START,
    CUSTOMER_AUTHENTICATE_BY_CODE_SUCCESS,
    CUSTOMER_ERROR,
    CUSTOMER_REQUEST_AUTH_CODE_START,
    CUSTOMER_REQUEST_AUTH_CODE_SUCCESS,
    CustomerAction
} from 'actions/customer/CustomerActionsTypes';
import { convertToCustomer, convertToSaveCustomerDto } from 'actions/customer/CustomerConverter';
import { AuthenticateCustomerByCodeResponseDto, CustomerDto } from 'actions/customer/CustomerDtos';
import { CustomerError, CustomerErrorType } from 'actions/customer/CustomerErrors';
import { convertToSavePetDto } from 'actions/pets/PetsConverter';
import { ApiClient, ApiPublicClient } from 'api/ApiClient';
import { serializeToFormData } from 'api/serializer';
import axios from 'axios';
import moment from 'moment-timezone';
import { ThunkAction } from 'redux-thunk';
import { RootState } from 'store';
import { RootAction } from 'types/Actions';
import { AlertScreen } from 'types/Alert';
import { Customer } from 'types/Customer';
import { Pet } from 'types/Pet';
import { getFirstAxiosError } from 'utils/axiosHelper';

const customersUrl = 'customers';
const lookupUrl = 'customer_lookup';
const lookupByPhoneUrl = 'customer_lookup_by_phone';
const requestSMSCodeUrl = 'generate_sms_code';
const loginWithSMSCodeUrl = 'generate_token';
const lookupTokenUrl = 'customer_lookup_by_token';

const errorHandler = (error: unknown): CustomerAction => {
    console.error(error);

    return {
        type: CUSTOMER_ERROR
    };
};

export const requestAuthenticationCodeThunk = (
    phone: string,
    alertScreen?: AlertScreen
): ThunkAction<void, RootState, null, RootAction> => {
    return async (dispatch, getState) => {
        dispatch(requestAuthenticationCodeStart());

        try {
            const marketplaceId = getState().marketplace.data.id;

            await ApiPublicClient.post(requestSMSCodeUrl, {
                phone,
                marketplace_id: marketplaceId
            });

            dispatch(requestAuthenticationCodeSuccess());
        } catch (error) {
            let errorMessage = 'Oops, something went wrong.';

            if (axios.isAxiosError(error) && error.response?.status === 404) {
                errorMessage = 'No client found with that phone number.';
            }

            dispatch(errorHandler(error));
            dispatch(addAlertThunk(errorMessage, alertScreen));
        }
    };
};

export const authenticateCustomerBySMSCodeThunk = (
    phone: string,
    code: string,
    alertScreen?: AlertScreen
): ThunkAction<Promise<{ token: string; customer: Customer }>, RootState, null, RootAction> => {
    return async (dispatch, getState) => {
        dispatch(authenticateByCodeStart());

        try {
            const marketplaceId = getState().marketplace.data.id;

            const {
                data: { token }
            } = await ApiPublicClient.post<AuthenticateCustomerByCodeResponseDto>(
                loginWithSMSCodeUrl,
                {
                    phone,
                    sms_code: code,
                    marketplace_id: marketplaceId
                }
            );

            dispatch(setAuthToken(token));

            const customerDto = await dispatch(getCustomerByTokenThunk(token, marketplaceId));

            const customer = convertToCustomer(customerDto);

            dispatch(authenticateByCodeSuccess(customer));

            return { token, customer };
        } catch (error) {
            let errorMesssage = 'Oops, something went wrong.';

            if (axios.isAxiosError(error) && error.response?.data.message === 'SMS Code expired') {
                errorMesssage = 'Code expired, please request a new one.';
            }

            if (axios.isAxiosError(error) && error.response?.data.message === 'SMS Code invalid') {
                errorMesssage = 'Invalid code, please try again.';
            }

            dispatch(errorHandler(error));
            dispatch(addAlertThunk(errorMesssage, alertScreen));

            throw error;
        }
    };
};

export const getCustomerByTokenThunk = (
    token: string,
    marketplaceId?: number
): ThunkAction<Promise<CustomerDto>, RootState, null, RootAction> => {
    return async () => {
        const { data } = await ApiPublicClient.post<CustomerDto>(lookupTokenUrl, {
            token,
            marketplace_id: marketplaceId
        });

        return data;
    };
};

export const lookupCustomerThunk = (
    email: string,
    marketplaceId?: number
): ThunkAction<Promise<Customer | null>, RootState, null, RootAction> => {
    return async () => {
        try {
            const response = await ApiPublicClient.post<CustomerDto>(
                lookupUrl,
                {
                    email
                },
                {
                    params: {
                        marketplace_id: marketplaceId
                    }
                }
            );

            return convertToCustomer(response.data);
        } catch (error) {
            if (axios.isAxiosError(error) && error.response?.status === 404) {
                return null;
            }

            throw error;
        }
    };
};

export const lookupCustomerByPhoneThunk = (
    phone: string,
    marketplaceId?: number
): ThunkAction<Promise<Customer | null>, RootState, null, RootAction> => {
    return async () => {
        try {
            const response = await ApiPublicClient.post<CustomerDto>(
                lookupByPhoneUrl,
                {
                    phone: phone.replace(/\D+/g, '')
                },
                {
                    params: {
                        marketplace_id: marketplaceId
                    }
                }
            );

            return convertToCustomer(response.data);
        } catch (error) {
            if (axios.isAxiosError(error) && error.response?.status === 404) {
                return null;
            }

            throw error;
        }
    };
};

export const saveCustomerThunk = (
    customer: Customer,
    channel: string,
    password?: string
): ThunkAction<Promise<Customer>, RootState, null, CustomerAction> => {
    return async (dispatch, getState) => {
        try {
            const marketplaceId = getState().marketplace.data.id;

            const url = customer.id ? `${customersUrl}/${customer.id}` : customersUrl;
            const apiMethod = customer.id ? ApiClient.patch : ApiClient.post;

            const formData = serializeToFormData({
                customer: convertToSaveCustomerDto(customer, marketplaceId, channel, password)
            });

            const response = await apiMethod<CustomerDto>(url, formData, {
                params: {
                    marketplace_id: marketplaceId
                }
            });

            return convertToCustomer(response.data);
        } catch (error) {
            dispatch(errorHandler(error));

            const errorMessage = getFirstAxiosError(error, 'Oops, something went wrong.');

            if (axios.isAxiosError(error)) {
                const data = error.response?.data;

                if ('phone' in data) {
                    throw new CustomerError(CustomerErrorType.PhoneAlreadyTaken);
                }

                if ('email' in data) {
                    throw new CustomerError(CustomerErrorType.EmailAlreadyTaken);
                }
            }

            throw new CustomerError(CustomerErrorType.Unknown, errorMessage);
        }
    };
};

export const updateCustomerSmsMarketingOptInThunk = (
    customerId: number,
    smsMarketingOptIn: boolean
): ThunkAction<Promise<void>, RootState, null, CustomerAction> => {
    return async (_dispatch, getState) => {
        const marketplaceId = getState().marketplace.data.id;

        const timestampField = smsMarketingOptIn
            ? { marketing_opt_in_at: moment().toISOString() }
            : { marketing_opt_out_at: moment().toISOString() };

        const formData = serializeToFormData({
            customer: {
                marketing_optout: !smsMarketingOptIn,
                ...timestampField
            }
        });

        await ApiClient.patch(`${customersUrl}/${customerId}`, formData, {
            params: {
                marketplace_id: marketplaceId
            }
        });
    };
};

export const saveCustomerPetsThunk = (
    customer: Customer,
    pets?: Array<Pet>
): ThunkAction<Promise<Customer | null>, RootState, null, RootAction> => {
    return async (dispatch, getState) => {
        try {
            const marketplaceId = getState().marketplace.data.id;

            const formData = serializeToFormData({
                customer: {
                    pets_attributes: pets?.map(pet => convertToSavePetDto(pet, marketplaceId))
                }
            });

            const response = await ApiClient.patch<CustomerDto>(
                `${customersUrl}/${customer.id}`,
                formData
            );

            return convertToCustomer(response.data);
        } catch (error) {
            dispatch(errorHandler(error));

            return null;
        }
    };
};

const requestAuthenticationCodeStart = (): CustomerAction => {
    return {
        type: CUSTOMER_REQUEST_AUTH_CODE_START
    };
};

const requestAuthenticationCodeSuccess = (): CustomerAction => {
    return {
        type: CUSTOMER_REQUEST_AUTH_CODE_SUCCESS
    };
};

const authenticateByCodeStart = (): CustomerAction => {
    return {
        type: CUSTOMER_AUTHENTICATE_BY_CODE_START
    };
};

const authenticateByCodeSuccess = (customer: Customer): CustomerAction => {
    return {
        type: CUSTOMER_AUTHENTICATE_BY_CODE_SUCCESS,
        payload: {
            customer
        }
    };
};
